import { ApiError, type PreparedRequest } from "./http-transport";
import type { HttpMethod } from "./types";

export type UploadProgress = {
  loaded: number;
  total: number;
  ratio: number;
};

export const supportsUploadProgress = (): boolean => typeof XMLHttpRequest !== "undefined";

const parseBody = (text: string): Record<string, unknown> => {
  try {
    return text.length === 0 ? {} : JSON.parse(text);
  } catch {
    return {};
  }
};

const failureFor = (xhr: XMLHttpRequest): ApiError => {
  const payload = parseBody(xhr.responseText);
  const message = typeof payload.message === "string" ? payload.message : "Request failed";
  return new ApiError(xhr.status || 500, message, payload.details as string[] | undefined, payload);
};

const reportProgress = (event: ProgressEvent, onProgress: (progress: UploadProgress) => void): void => {
  const total = event.lengthComputable ? event.total : 0;
  onProgress({ loaded: event.loaded, total, ratio: total > 0 ? event.loaded / total : 0 });
};

export const sendRequestWithProgress = <T>(method: HttpMethod, prepared: PreparedRequest, onProgress: (progress: UploadProgress) => void): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, prepared.url);
    for (const [key, value] of Object.entries(prepared.headers)) {
      xhr.setRequestHeader(key, value);
    }
    xhr.upload.onprogress = (event) => reportProgress(event, onProgress);
    xhr.onerror = () => reject(new ApiError(0, "Request failed"));
    xhr.onabort = () => reject(new ApiError(0, "Request aborted"));
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(failureFor(xhr));
        return;
      }
      resolve((xhr.status === 204 ? undefined : parseBody(xhr.responseText)) as T);
    };
    xhr.withCredentials = prepared.credentials === "include";
    xhr.send(prepared.body ?? null);
  });
